import { Text, View, ScrollView, TouchableOpacity, Linking, Platform } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useMemo } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Unicon from '@/components/General/Unicon';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ModalWrapper from '@/components/General/ModalWrapper';
import { showAlert } from '@/utils/showAlert';
import { deleteLocation, getFutureLocationUsageCount } from '@/data/locations';

export default function LocationDetailModal() {
    const { locationID } = useLocalSearchParams();
    const router = useRouter();
    const { data: location, isLoading } = Query.locations.byId({ id: locationID, options: { enabled: !!locationID && locationID !== 'undefined' } });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const queryClient = useQueryClient();

    const deleteLocationMutation = useMutation({
        mutationFn: deleteLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.locations.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
            router.back();
        },
    });

    // Warn (with a count) when the location still has upcoming bookings.
    const confirmDelete = useCallback(async () => {
        const futureCount = await getFutureLocationUsageCount(locationID).catch(() => 0);
        showAlert({
            title: 'Delete Location?',
            subtitle: futureCount > 0
                ? `${futureCount} upcoming event${futureCount === 1 ? ' is' : 's are'} still booked in "${location?.name}". Those events won't be deleted, but the room will no longer be selectable.\n\nDelete this location anyway?`
                : `Are you sure you want to delete "${location?.name}"? This cannot be undone.`,
            confirmText: 'Delete',
            isDestructive: true,
            onConfirm: () => deleteLocationMutation.mutate(locationID),
        });
    }, [locationID, location?.name, deleteLocationMutation]);

    const fullAddress = useMemo(() => {
        const a = location?.Address;
        if (!a) return null;
        const parts = [a.street, a.streetLine2, a.city, a.state && a.zip ? `${a.state} ${a.zip}` : (a.state || a.zip)].filter(Boolean);
        return parts.join(', ');
    }, [location?.Address]);

    const openMaps = useCallback(() => {
        if (!fullAddress) return;
        const encoded = encodeURIComponent(fullAddress);
        const url = Platform.OS === 'ios'
            ? `maps://?q=${encoded}`
            : `https://maps.google.com/?q=${encoded}`;
        Linking.openURL(url);
    }, [fullAddress]);

    const canEdit = loggedInUser?.can?.('Edit Events') || loggedInUser?.can?.('Manage Locations');
    const canDelete = loggedInUser?.can?.('Delete Events') || loggedInUser?.can?.('Manage Locations');

    if (isLoading) {
        return (
            <ModalWrapper title="Location">
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" />
                </View>
            </ModalWrapper>
        );
    }

    if (!location) {
        return (
            <ModalWrapper title="Location">
                <EmptyListPlaceholder type="404" message="Location not found" />
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper
            title="Location Details"
            cancelText="Close"
            noPadding
        >
            <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                {/* Hero Card */}
                <View style={styles.heroCard}>
                    <View style={styles.heroIconBadge}>
                        <Unicon name="location" size={28} colorVariant="secondary" />
                    </View>
                    <View style={styles.heroInfo}>
                        <Text style={styles.heroName}>{location.name}</Text>
                        {location.detailedArea ? (
                            <Text style={styles.heroSub}>{location.detailedArea}</Text>
                        ) : null}
                    </View>
                    {canEdit ? (
                        <TouchableOpacity
                            style={styles.heroEditBtn}
                            onPress={() => router.push({ pathname: '/events/locations/create', params: { id: locationID } })}
                        >
                            <Unicon name="create-outline" size={18} colorVariant="primary" />
                        </TouchableOpacity>
                    ) : null}
                </View>

                {/* Address Section */}
                <View style={styles.section}>
                    <Text style={styles.sectionLabel}>Address</Text>
                    {location.Address ? (
                        <View style={styles.card}>
                            {/* Address fields */}
                            {location.Address.street ? (
                                <View style={styles.addressRow}>
                                    <Unicon name="home-outline" size={16} colorVariant="textSecondary" />
                                    <View style={styles.addressText}>
                                        <Text style={styles.addressStreet}>{location.Address.street}</Text>
                                        {location.Address.streetLine2 ? (
                                            <Text style={styles.addressLine2}>{location.Address.streetLine2}</Text>
                                        ) : null}
                                        <Text style={styles.addressCityState}>
                                            {[location.Address.city, location.Address.state, location.Address.zip].filter(Boolean).join(', ')}
                                        </Text>
                                    </View>
                                </View>
                            ) : null}

                            <View style={styles.addressActions}>
                                {fullAddress ? (
                                    <TouchableOpacity style={styles.addressActionBtn} onPress={openMaps}>
                                        <Unicon name="map-outline" size={14} colorVariant="primary" />
                                        <Text style={styles.addressActionText}>Open in Maps</Text>
                                    </TouchableOpacity>
                                ) : null}
                                <TouchableOpacity
                                    style={styles.addressActionBtn}
                                    onPress={() => router.push({ pathname: '/addresses/' + location.Address.id })}
                                >
                                    <Unicon name="information-circle-outline" size={14} colorVariant="primary" />
                                    <Text style={styles.addressActionText}>Address Details</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    ) : (
                        <View style={styles.emptyCard}>
                            <Text style={styles.emptyCardText}>No address linked to this location.</Text>
                            {canEdit ? (
                                <TouchableOpacity
                                    onPress={() => router.push({ pathname: '/events/locations/create', params: { id: locationID } })}
                                >
                                    <Text style={styles.linkText}>Add one →</Text>
                                </TouchableOpacity>
                            ) : null}
                        </View>
                    )}
                </View>

                {canDelete ? (
                    <TouchableOpacity
                        testID="delete-location-button"
                        style={styles.deleteButton}
                        disabled={deleteLocationMutation.isPending}
                        onPress={confirmDelete}
                    >
                        {deleteLocationMutation.isPending ? (
                            <ActivityIndicator size="small" />
                        ) : (
                            <Text style={styles.deleteButtonText}>Delete This Location</Text>
                        )}
                    </TouchableOpacity>
                ) : null}

            </ScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Content
    content: {
        padding: 16,
        paddingBottom: 60,
    },

    // Hero Card
    heroCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 20,
        marginBottom: 20,
        boxShadow: theme.shadows.card,
        gap: 16,
    },
    heroEditBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroIconBadge: {
        width: 60,
        height: 60,
        borderRadius: 14,
        backgroundColor: theme.colors.secondaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroInfo: {
        flex: 1,
    },
    heroName: {
        fontSize: 20,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
        marginBottom: 4,
    },
    heroSub: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },

    // Section
    section: {
        marginBottom: 20,
    },
    sectionLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        marginBottom: 10,
        marginLeft: 4,
    },

    // Card
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        padding: 16,
        boxShadow: theme.shadows.card,
    },
    emptyCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        padding: 16,
        boxShadow: theme.shadows.card,
        alignItems: 'center',
        gap: 8,
    },
    emptyCardText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        fontStyle: 'italic',
    },
    linkText: {
        fontSize: 14,
        color: theme.colors.primary,
        fontFamily: theme.fonts.bold,
    },

    // Address
    addressRow: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'flex-start',
        marginBottom: 14,
    },
    addressText: {
        flex: 1,
        gap: 2,
    },
    addressStreet: {
        fontSize: 15,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    addressLine2: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    addressCityState: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    addressActions: {
        flexDirection: 'row',
        gap: 10,
        flexWrap: 'wrap',
    },
    addressActionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primaryContainer,
        backgroundColor: theme.colors.primaryContainer,
    },
    addressActionText: {
        fontSize: 13,
        color: theme.colors.primary,
        fontFamily: theme.fonts.bold,
    },

    // Delete
    deleteButton: {
        paddingVertical: 16,
        marginTop: 8,
        alignItems: 'center',
    },
    deleteButtonText: {
        color: theme.colors.error,
        fontFamily: theme.fonts.heading,
        fontSize: 15,
    },
}));
